import { useEffect, useState } from 'react';
import { getHandlingTimeWorkWindow, type HandlingTimeWorkWindowResponse, type HandlingTimeWorkWindowStep } from '../handlingTime.api';

/**
 * Behandeltijd-rapport, CAP-2 en CAP-3 — het werkvenster ophalen.
 *
 * Met de hand, zoals `useHandlingTimePerEtiket`: de toetsen van het behandeltijdscherm renderen zonder
 * `QueryClientProvider`. Geen herpoging; een storing wordt per blok als "niet beschikbaar" getoond.
 *
 * Elk antwoord hangt aan de sleutel van de vraag (periode en stap). Een antwoord van een eerdere vraag
 * is daardoor nooit de stand van de nieuwe vraag: die toont "laden" tot zijn eigen antwoord er is.
 */
export interface WerkvensterLezing {
  data: HandlingTimeWorkWindowResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

interface Resultaat {
  sleutel: string;
  data?: HandlingTimeWorkWindowResponse;
  error?: Error;
}

export function useHandlingTimeWorkWindow(
  period: { from?: string; to?: string },
  options: { enabled?: boolean; step?: HandlingTimeWorkWindowStep } = {},
): WerkvensterLezing {
  const { from, to } = period;
  const { enabled = true, step = 'both' } = options;
  const actief = enabled && Boolean(from) && Boolean(to);
  const sleutel = actief ? `${from}|${to}|${step}` : null;

  const [resultaat, setResultaat] = useState<Resultaat | null>(null);

  useEffect(() => {
    if (sleutel === null) return;
    // Een antwoord dat binnenkomt nadat de periode of de stap al verwisseld is, wordt weggegooid.
    let levend = true;
    getHandlingTimeWorkWindow({ from: from!, to: to! }, step)
      .then((antwoord) => { if (levend) setResultaat({ sleutel, data: antwoord }); })
      .catch((oorzaak: unknown) => {
        if (levend) setResultaat({ sleutel, error: oorzaak instanceof Error ? oorzaak : new Error(String(oorzaak)) });
      });
    return () => { levend = false; };
  }, [sleutel, from, to, step]);

  // Alleen een resultaat van DEZE sleutel telt; alles anders is nog laden (of niet gevraagd).
  const huidig = sleutel !== null && resultaat?.sleutel === sleutel ? resultaat : null;
  if (sleutel === null) return { data: undefined, isLoading: false, isError: false, error: null };
  return {
    data: huidig?.data,
    isLoading: huidig === null,
    isError: huidig?.error !== undefined,
    error: huidig?.error ?? null,
  };
}
